import EGDayClosure, { HolidayDateGlyph, EGClosureHistory } from "@/components/scheduling/EGDayClosure";
import { dayClosure, openDayShifts } from "@/lib/egDayClosures";
import { hasShiftTimes, shiftHours, periodRoster } from "@/lib/egScheduleDisplay";
import EGScheduleOverview from "@/components/scheduling/EGScheduleOverview";
import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useVesselNavigation } from "@/components/toto/VesselNavigation";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import {
  Upload, Plus, ChevronLeft, ChevronRight, ChevronDown, Send, CheckCircle2, RotateCcw,
  XCircle, Trash2, Copy, Eraser, Info, ClipboardList, AlertTriangle, FileDown, Users, Undo2,
} from "lucide-react";
import EGShiftSignal, { EGShiftLegend } from "@/components/scheduling/EGShiftSignal";
import { getShiftPresentation, EG_SHIFT_COLORS } from "@/lib/egShiftPresentation";
import EGPrintPacket from "@/components/scheduling/EGPrintPacket";
import EGScheduleExport from "@/components/scheduling/EGScheduleExport";
import "./ScheduleEGSpatial.css";
import "./toto-workspaces.css";
import "./toto-workspaces-refinement.css";
import "./schedule-eg-vessel.css";
import "./schedule-eg-inspector.css";
import "./schedule-eg-calendar.css";

const InspectorContext = createContext(null);
const SCHEDULE_BOUTIQUES = [
  {code:"PLT",name:"Plantation",color:"#009B3A"}, {code:"FLL",name:"Fort Lauderdale",color:"#0047FF"},
  {code:"WYN",name:"Wynwood",color:"#EF008C"}, {code:"PNC",name:"Pinecrest",color:"#FF6200"},
];

/**
 * EG Scheduling — monthly builder with a weekly-grid editor.
 *
 * Deadline states derive from the 15th-of-preceding-month rule and shift tone
 * as the date approaches. Once published, edits require an amendment reason,
 * and each amendment auto-queues a Zenoti update task.
 */

const STATUS_STYLES = {
  draft:              { dot: "bg-stone-400",   label: "Draft" },
  ready_for_review:   { dot: "bg-amber-500",   label: "Ready for review" },
  submitted:          { dot: "bg-amber-500",   label: "Submitted to Katrina" },
  changes_requested:  { dot: "bg-rose-500",    label: "Changes requested" },
  approved:           { dot: "bg-emerald-500", label: "Approved · awaiting publish" },
  published:          { dot: "bg-emerald-500", label: "Active" },
};



// The roster visible in the weekly Plantation schedule. It is deliberately a
// scheduling default, rather than a payroll employee query. Once a GM saves
// the roster it becomes a record on that individual schedule period.
const PLANTATION_EG_ROSTER = ["Alani", "Eboni", "Heather", "Kendah", "Kristin", "Lilli-Ann LeConte", "Damon"];
const PLANTATION_EG_DIRECTORY_IDS = {
  alani: "1008926", eboni: "1009838", heather: "1009315", kendah: "1009764", kristin: "1008251", lilliann: "1009911",
};

// Approved PLT Experience Guide source: five complete weeks, retained on the
// September draft even where a printed week crosses a month boundary.
const SEP_2026_SOURCE_WEEKS = [
  ["2026-08-31", {
    damon:["off","o9-5","o9-5","o9-5","o9-5","o8-4","off"],
    alani:["o9-530","o9-530","o9-530","off","off","off","m10-630"],
    eboni:["off","off","m10-630","o9-530","o9-530","off","o8-330"],
    heather:["off","off","m11-730","m10-630","m10-630","off","off"],
    kendah:["c1-930","c1-930","off","off","m11-730","m9-530","m12-730"],
    kristin:["off","off","c1-930","c1-930","c1-930","c1-930","cross"],
    lilliann:["m11-730","m11-730","off","off","off","c12-830","c12-830"],
  }],
  ["2026-09-07", {
    damon:["off","o9-5","o9-5","o9-5","o9-5","o8-4","off"],
    alani:["o9-530","o9-530","o9-530","off","off","o8-430","m10-530"],
    eboni:["off","off","m10-630","o9-530","o9-530","off","o8-330"],
    heather:["m10-630","m10-630","off","off","off","off","off"],
    kendah:["c1-930","c1-930","off","off","m11-730","m11-730","m12-730"],
    kristin:["off","off","c1-930","c1-930","c1-930","c12-830","cross"],
    lilliann:["off","off","m11-730","m11-730","m10-630","c12-830","c1-830"],
  }],
  ["2026-09-14", {
    damon:["off","o9-5","o9-5","o9-5","o9-5","o8-4","off"],
    alani:["off","off","m10-630","o9-530","o9-530","request","request"],
    eboni:["off","off","m10-630","o9-530","o9-530","off","request"],
    heather:["m10-630","m10-630","off","off","off","off","off"],
    kendah:["c1-930","c1-930","off","off","m11-730","m10-630","m12-730"],
    kristin:["off","off","c1-930","c1-930","c1-930","c12-830","o8-330"],
    lilliann:["off","off","m11-730","m11-730","m10-630","c11-730","c1-830"],
  }],
  ["2026-09-21", {
    damon:["off","o9-5","o9-5","o9-5","o9-5","o8-4","off"],
    alani:["o9-530","o9-530","off","off","o9-530","o8-430","m9-430"],
    eboni:["request","off","o9-530","o9-530","m10-630","off","m11-730"],
    heather:["off","off","m10-630","m10-630","off","off","off"],
    kendah:["c1-930","c1-930","off","off","m11-730","m11-730","c1-830"],
    kristin:["m10-530","m10-530","request","request","c1-930","c12-830","o8-430"],
    lilliann:["off","off","c1-930","c1-930","c1-930","off","c12-830"],
  }],
  ["2026-09-28", {
    damon:["off","o9-5","o9-5","o9-5","o9-5","o8-4","off"],
    alani:["o9-530","o9-530","off","off","o9-530","o8-430","m10-530"],
    eboni:["off","off","o9-530","o9-530","m10-630","off","o9-430"],
    heather:["m10-630","m10-630","off","off","off","off","off"],
    kendah:["c1-930","c1-930","off","off","m11-730","m11-730","c1-830"],
    kristin:["off","off","c1-930","c1-930","c1-930","c12-830","o8-430"],
    lilliann:["off","off","m10-630","m10-630","c1-930","off","c12-830"],
  }],
];

const SEP_2026_SHIFT_CODES = {
  off:{ shift_type:"unavailable" }, request:{ shift_type:"requested" }, cross:{ shift_type:"cross_center" },
  "o9-5":{ shift_type:"opening",start:"09:00",end:"17:00" }, "o8-4":{ shift_type:"opening",start:"08:00",end:"16:00" }, "o8-330":{ shift_type:"opening",start:"08:00",end:"15:30" }, "o8-430":{ shift_type:"opening",start:"08:00",end:"16:30" }, "o9-530":{ shift_type:"opening",start:"09:00",end:"17:30" }, "o9-430":{ shift_type:"opening",start:"09:00",end:"16:30" },
  "m9-530":{ shift_type:"mid_shift",start:"09:00",end:"17:30" }, "m9-430":{ shift_type:"mid_shift",start:"09:00",end:"16:30" }, "m10-630":{ shift_type:"mid_shift",start:"10:00",end:"18:30" }, "m10-530":{ shift_type:"mid_shift",start:"10:00",end:"17:30" }, "m11-730":{ shift_type:"mid_shift",start:"11:00",end:"19:30" }, "m12-730":{ shift_type:"mid_shift",start:"12:00",end:"19:30" },
  "c1-930":{ shift_type:"closing",start:"13:00",end:"21:30" }, "c12-830":{ shift_type:"closing",start:"12:00",end:"20:30" }, "c1-830":{ shift_type:"closing",start:"13:00",end:"20:30" }, "c11-730":{ shift_type:"closing",start:"11:00",end:"19:30" },
};

const SHIFT_TYPES = {
  opening: { label: "Opening", swatch: "bg-lime-400", card: "bg-lime-100 border border-lime-200 hover:bg-lime-200", text: "text-lime-900" },
  mid_shift: { label: "Mid-shift", swatch: "bg-yellow-400", card: "bg-yellow-100 border border-yellow-200 hover:bg-yellow-200", text: "text-yellow-900" },
  closing: { label: "Closing", swatch: "bg-sky-500", card: "bg-sky-100 border border-sky-200 hover:bg-sky-200", text: "text-sky-900" },
  training: { label: "Training", swatch: "bg-violet-500", card: "bg-violet-100 border border-violet-200 hover:bg-violet-200", text: "text-violet-900" },
  requested: { label: "Requested", swatch: "bg-red-500", card: "bg-red-100 border border-red-200 hover:bg-red-200", text: "text-red-900" },
  cross_center: { label: "Cross-Center", swatch: "bg-orange-500", card: "bg-orange-100 border border-orange-200 hover:bg-orange-200", text: "text-orange-900" },
  unavailable: { label: "OFF", swatch: "bg-red-500", card: "bg-red-100 border border-red-200 hover:bg-red-200", text: "text-red-900" },
};

function normalizeShiftType(value) {
  return Object.prototype.hasOwnProperty.call(SHIFT_TYPES, value) ? value : null;
}

function shiftTypeFor(shift) {
  const saved = normalizeShiftType(shift?.shift_type);
  if (saved) return saved;
  const hour = Number(String(shift?.start || "12:00").split(":")[0]);
  if (hour < 10) return "opening";
  if (hour < 12) return "mid_shift";
  return "closing";
}

function isDateOnlyShiftType(value) {
  return ["requested", "cross_center", "unavailable"].includes(value);
}

function rosterNameKey(person) {
  return String(person?.employee_name || person?.name || "").trim().split(/\s+/)[0].toLowerCase();
}

function rosterIdentityKey(person) {
  return String(person?.employee_name || person?.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function compactEmployeeName(person) {
  const parts = String(person?.employee_name || person?.name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0]?.toLowerCase() === "damon" ? "Damon M." : (parts[0] || "");
  return `${parts[0]} ${parts.at(-1).charAt(0).toUpperCase()}.`;
}

function isSalariedGeneralManager(person) {
  const details = [person?.role, person?.job_title, person?.title, person?.employment_type]
    .filter(Boolean).join(" ").toLowerCase();
  return /general\s*manager|\bgm\b|salaried/.test(details) || rosterNameKey(person) === "damon";
}


function formatHours(hours) {
  return `${Math.round(hours * 10) / 10} hrs`;
}

function defaultPlantationRoster(directory) {
  return PLANTATION_EG_ROSTER.map((name) => {
    const known = directory.find((person) => rosterIdentityKey(person) === rosterIdentityKey({ name }))
      || directory.find((person) => rosterNameKey(person) === rosterNameKey({ name }));
    // Keep authoritative directory names for short roster aliases, while the
    // explicit Lilli-Ann LeConte roster entry corrects the legacy spelling.
    const key = rosterNameKey({ name }).replace(/[^a-z0-9]/g, "");
    const sourceEmployeeId = PLANTATION_EG_DIRECTORY_IDS[key];
    return known
      ? { ...known, employee_id: sourceEmployeeId || known.employee_id, employee_name: name.includes(" ") ? name : known.employee_name }
      : { employee_id: sourceEmployeeId || `plt-eg-${name.toLowerCase()}`, employee_name: name };
  });
}

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function firstOfNextMonth() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const next = new Date(y, m + 1, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-01`;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseISO(s) { return new Date(s + "T00:00:00"); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeekMonday(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - dow);
  return x;
}

function PeriodPicker({ boutique, periods = [], onCreated, onCancel }) {
  const [start, setStart] = useState(firstOfNextMonth());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const create = async () => {
    const existing = periods.find(p => p.period_start?.slice(0,7) === start.slice(0,7));
    if (existing) { onCreated(existing); return; }
    setSaving(true); setErr("");
    try {
      const r = await api.post("/scheduling/periods", { boutique_code: boutique, mode: "EG", period_start: start });
      onCreated(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create period.");
    } finally { setSaving(false); }
  };

  const existing = periods.find(p => p.period_start?.slice(0,7) === start.slice(0,7));
  return <section className="eg-month-picker" data-testid="eg-new-period" aria-label="Choose another schedule month">
    <label htmlFor="eg-month-input">Month to prepare</label>
    <div className="eg-month-picker__controls">
      <input id="eg-month-input" type="month" value={start.slice(0,7)} onChange={e=>setStart(e.target.value ? `${e.target.value}-01` : "")} data-testid="eg-new-start"/>
      <button type="button" onClick={create} disabled={saving || !start} data-testid="eg-new-create">{saving ? "Opening…" : existing ? "Open" : "Prepare"}</button>
      {onCancel && <button type="button" onClick={onCancel}>Cancel</button>}
    </div>
    {err && <p role="alert" data-testid="eg-new-error">{err}</p>}
  </section>;

}

function ShiftCell({
  shift, employee, dateStr, onSave, onDelete, canEdit, canAmend, needsAmendment,
  onDragStart, onDragEnd, onDragEnter, onDragLeave, isDropTarget, onDropShift,
  copiedShift, movingShift, onCopyShift, onBeginMove, onPasteShift, onPlaceMove,
}) {
  const inspector = useContext(InspectorContext);
  const cellKey = `${inspector.periodId}|${employee.employee_id}|${dateStr}|${shift?.id || "new"}`;
  const open = inspector.selected === cellKey;
  const setOpen = value => inspector.select(value ? cellKey : null);
  const initialValues = () => inspector.drafts.current.get(cellKey) || {
    start: shift?.start || "09:00", end: shift?.end || "17:00", notes: shift?.notes || "",
    shiftType: shiftTypeFor(shift), hasSpecificHours: hasShiftTimes(shift),
    requested: getShiftPresentation(shift).requested, mod: shift?.mod === true, reason: "",
  };
  const [values, setValues] = useState(initialValues);
  const {start,end,notes,shiftType,hasSpecificHours,requested,mod,reason} = values;
  const update = (field, value) => setValues(current => {
    const next = {...current, [field]: value}; inspector.drafts.current.set(cellKey,next); return next;
  });
  const setStart = value => update("start",value), setEnd = value => update("end",value);
  const setNotes = value => update("notes",value), setShiftType = value => update("shiftType",value);
  const setHasSpecificHours = value => update("hasSpecificHours",value), setRequested = value => update("requested",value);
  const setReason = value => update("reason",value);
  const [saving, setSaving] = useState(false), [err, setErr] = useState("");
  const anchorRef = useRef(null), editorRef = useRef(null);
  useEffect(() => { if (open) { setValues(initialValues()); setErr(""); } }, [open, shift]); // eslint-disable-line react-hooks/exhaustive-deps
  const canOpen = shift ? (canEdit || canAmend) : canEdit;
  const isMoveTarget = Boolean(!shift && movingShift && canEdit);
  useLayoutEffect(() => {
    if (!open || !inspector.host || !editorRef.current) return;
    const editor = editorRef.current, anchor = anchorRef.current;
    editor.querySelector("select")?.focus();
    return () => { if (editor.contains(document.activeElement)) anchor?.focus(); };
  }, [open, inspector.host]);

  const save = async () => {
    if (needsAmendment && !reason.trim()) { setErr("An amendment reason is required."); return; }
    setSaving(true); setErr("");
    try {
      const includeHours = !isDateOnlyShiftType(shiftType) || hasSpecificHours;
      await onSave({
        id: shift?.id, date: dateStr, employee_id: employee.employee_id,
        ...(includeHours ? { start, end } : {}),
        break_start: shift?.break_start, break_end: shift?.break_end, location: shift?.location,
        notes, shift_type: shiftType, mod, requested: shiftType === "requested" || requested, amendment_reason: needsAmendment ? reason.trim() : undefined,
      });
      inspector.drafts.current.delete(cellKey);
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save shift.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (needsAmendment && !reason.trim()) { setErr("An amendment reason is required."); return; }
    setSaving(true); setErr("");
    try {
      await onDelete(shift.id, needsAmendment ? reason.trim() : undefined);
      inspector.drafts.current.delete(cellKey);
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not remove shift.");
    } finally { setSaving(false); }
  };

  const signal = getShiftPresentation(shift);

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        draggable={Boolean(shift && canEdit && !needsAmendment)}
        onDragStart={(event) => { if (shift && onDragStart) onDragStart(event, shift); }}
        onDragEnd={onDragEnd}
        onDragOver={(event) => { if (canEdit && !shift) event.preventDefault(); }}
        onDragEnter={() => { if (canEdit && !shift && onDragEnter) onDragEnter(); }}
        onDragLeave={() => { if (isDropTarget && onDragLeave) onDragLeave(); }}
        onDrop={(event) => { if (canEdit && !shift && onDropShift) { event.preventDefault(); onDropShift(employee, dateStr); } }}
        onClick={() => {
          if (!canOpen) return;
          if (isMoveTarget) { onPlaceMove(employee, dateStr); return; }
          setOpen(true);
        }}
        disabled={!canOpen}
        data-testid={`eg-cell-${employee.employee_id}-${dateStr}`}
        title={[employee.employee_name, dateStr, signal.label, signal.timeLabel, signal.requested ? "Requested" : "", shift?.notes].filter(Boolean).join(" · ")}
        aria-label={[employee.employee_name, dateStr, signal.label, signal.timeLabel, shift?.mod ? "MOD" : "", signal.requested && shift?.shift_type !== "requested" ? "Requested" : ""].filter(Boolean).join(", ")}
        className={`eg-signal-cell ${open ? "is-selected" : ""} w-full text-left ${signal.requested ? "eg-shift-cell--requested" : ""} ${isDropTarget || isMoveTarget ? "eg-signal-cell--target" : ""} ${shift && canEdit && !needsAmendment ? "cursor-grab active:cursor-grabbing" : canOpen ? "cursor-pointer" : "cursor-default"}`}
      >
        {isDropTarget || isMoveTarget ? <span>{isDropTarget ? "Drop shift here" : "Place shift here"}</span> : <EGShiftSignal shift={shift} />}

      </button>
      {open && inspector.host && createPortal(
          <section ref={editorRef} role="region"
            aria-label={`Edit shift for ${employee.employee_name} on ${dateStr}`}
            onKeyDown={event => { if(event.key === "Escape") { event.stopPropagation(); setOpen(false); anchorRef.current?.focus(); } }}
            className="eg-focused-editor" data-testid={`eg-cell-editor-${employee.employee_id}-${dateStr}`}>
            <div className="eg-focused-editor__caption"><span>Edit shift</span><button type="button" aria-label="Close shift editor" onClick={() => {setOpen(false); anchorRef.current?.focus();}}><XCircle size={18}/></button></div>
            <header className="eg-shift-editor__header">
              <h2>{employee.employee_name}</h2>
              <p>{new Date(`${dateStr}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
            </header>
            <label className="block text-[10px] label-eyebrow text-stone-500">
              Shift category
              <select value={shiftType} onChange={(e) => {
                const nextType = e.target.value;
                setShiftType(nextType);
                if (nextType === "requested") setRequested(true);
                if (!isDateOnlyShiftType(nextType)) setHasSpecificHours(true);
              }} data-testid="eg-shift-type"
                className="mt-1 w-full px-2 py-1 text-[11px] rounded subtle-border bg-white outline-none">
                {Object.entries(SHIFT_TYPES).map(([value, config]) => <option key={value} value={value}>{config.label}</option>)}
              </select>
            </label>
            <label className="eg-shift-editor__availability-toggle">
              <input type="checkbox" checked={shiftType === "requested" || requested} disabled={shiftType === "requested"} onChange={(e) => setRequested(e.target.checked)} data-testid="eg-shift-requested" />
              <span>Requested</span>
            </label>
            <label className="eg-shift-editor__availability-toggle"><input type="checkbox" checked={mod} onChange={e=>update("mod",e.target.checked)} data-testid="eg-shift-mod"/><span>MOD</span></label>
            {shiftType === "requested" && <p className="eg-shift-editor__availability-copy">Choose a shift category to give this request a category color. Its hours stay unchanged.</p>}
            {isDateOnlyShiftType(shiftType) && (
              <label className="eg-shift-editor__availability-toggle">
                <input type="checkbox" checked={hasSpecificHours} onChange={(e) => setHasSpecificHours(e.target.checked)} />
                <span>Specific hours</span>
              </label>
            )}
            {(!isDateOnlyShiftType(shiftType) || hasSpecificHours) ? (
              <div className="eg-focused-editor__times">
                <label>Start<input type="time" aria-label="Shift start time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="eg-shift-start"
                  className="w-full px-2 py-1 text-[11px] rounded subtle-border outline-none" />
                </label><label>End<input type="time" aria-label="Shift end time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="eg-shift-end"
                  className="w-full px-2 py-1 text-[11px] rounded subtle-border outline-none" /></label>
              </div>
            ) : <p className="eg-shift-editor__availability-copy">This records availability only. It adds no paid hours.</p>}
            <label className="eg-focused-editor__notes">Notes (optional)<textarea aria-label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="eg-shift-notes"
              className="w-full px-2 py-1 text-[11px] rounded subtle-border outline-none" /></label>
            {needsAmendment && (
              <div className="bg-amber-50 rounded-lg p-2 space-y-1">
                <div className="flex items-center gap-1 text-[10px] label-eyebrow text-amber-700">
                  <AlertTriangle size={10} strokeWidth={1.5} /> Amendment reason (required)
                </div>
                <input type="text" aria-label="Amendment reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. approved time-off coverage" data-testid="eg-amendment-reason"
                  className="w-full px-2 py-1 text-[11px] rounded bg-white outline-none" />
              </div>
            )}
            {err && <div role="alert" className="text-[11px] text-rose-600">{err}</div>}
            <div className="eg-focused-editor__actions">
              <div>{shift && canEdit && !needsAmendment && <>
                <button onClick={() => {onCopyShift(shift);setOpen(false);}}>Copy</button>
                <button onClick={() => {onBeginMove(shift);setOpen(false);}}>Move…</button>
              </>}</div>
              <button onClick={() => {setOpen(false);anchorRef.current?.focus();}}>Cancel</button>
              <button onClick={save} disabled={saving} data-testid={`eg-shift-save-${dateStr}`} className="eg-focused-editor__save">{saving ? "Saving…" : "Save"}</button>
            </div>
            {shift && <button onClick={remove} disabled={saving} data-testid={`eg-shift-delete-${dateStr}`} className="eg-focused-editor__remove"><Trash2 size={14}/> Remove</button>}
            {!shift && copiedShift && canEdit && (
              <button onClick={() => { onPasteShift(employee, dateStr); setOpen(false); }} className="w-full text-[11px] px-2 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
                Paste copied shift
              </button>
            )}
          </section>
      , inspector.host)}
    </div>
  );
}

function WeekGrid({ period, team, weekStart, canEdit, canAmend, isPublished, onSaveShift, onDeleteShift, onDayOptions }) {
  const [copiedShift, setCopiedShift] = useState(null);
  const [movingShift, setMovingShift] = useState(null);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const shiftByCell = useMemo(() => {
    const m = {};
    for (const s of openDayShifts(period?.current_version)) (m[`${s.employee_id}|${s.date}`] ||= []).push(s);
    return m;
  }, [period?.current_version]);

  // A salaried GM is the schedule's anchor, not an afterthought at the end of
  // the roster. Keep that person first without changing the saved roster order
  // for everyone else.
  const orderedTeam = useMemo(() => [...team].sort((a, b) => {
    const aSalaried = isSalariedGeneralManager(a);
    const bSalaried = isSalariedGeneralManager(b);
    if (aSalaried === bSalaried) return 0;
    return aSalaried ? -1 : 1;
  }), [team]);

  const pasteShift = async (employee, date) => {
    if (!copiedShift) return;
    await onSaveShift({ ...copiedShift, employee_id: employee.employee_id, date });
  };

  const placeMove = async (employee, date) => {
    if (!movingShift) return;
    await onSaveShift({ ...movingShift, employee_id: employee.employee_id, date });
    setMovingShift(null);
  };

  const weeklyHoursFor = (guide) => days.reduce(
    (guideTotal, day) => guideTotal + (shiftByCell[`${guide.employee_id}|${isoDate(day)}`] || []).reduce((sum, shift) => sum + shiftHours(shift), 0),
    0,
  );
  const countedHours = orderedTeam
    .filter((guide) => !isSalariedGeneralManager(guide))
    .reduce((total, guide) => total + weeklyHoursFor(guide), 0);
  return (
    <div className="eg-guide-scheduler">
      <header><p>Build the whole team’s week</p><span>{movingShift ? "Choose an empty day for the move." : copiedShift ? "Choose an empty day to paste the copied shift." : "Set the full Experience Guide team in one view."}</span></header>
      {orderedTeam.length === 0 ? <p className="eg-guide-scheduler__empty">No active Experience Guides at {period?.boutique_code}.</p> : <>
        <section className="eg-guide-scheduler__week" aria-label="Experience Guide weekly schedule">
          <div className="eg-guide-scheduler__week-heading"><div><p>Experience Guides</p><span>{canEdit ? "Select a day to add or edit a shift" : "Read-only"}</span></div><small>{formatHours(countedHours)} scheduled this week</small></div>
          <div className="eg-guide-scheduler__week-matrix" style={{"--eg-guide-count":orderedTeam.length, gridTemplateRows:`30px ${orderedTeam.map(guide => days.some(day => (shiftByCell[`${guide.employee_id}|${isoDate(day)}`] || []).length > 1) ? "minmax(54px,1.3fr)" : "minmax(36px,1fr)").join(" ")}`}}>
            <div className="eg-guide-scheduler__matrix-heading">Staff</div>
            {days.map((day) => {const date=isoDate(day),closure=dayClosure(period.current_version,date),inMonth=date>=period.period_start&&date<=period.period_end;return <button type="button" key={date} className={`eg-guide-scheduler__matrix-heading eg-day-heading${closure?" is-holiday":""}`} disabled={!(canEdit||canAmend)||!inMonth} onClick={()=>onDayOptions(date)} aria-label={`Holiday options for ${date}${closure?", closed":""}`}><strong>{closure&&<HolidayDateGlyph glyph={closure.glyph}/ >}{day.toLocaleDateString("en-US", { weekday: "short" })}{closure&&` ${day.getMonth()+1}/${day.getDate()}`}</strong><span>{closure?"Holiday":`${day.getMonth()+1}/${day.getDate()}`}</span></button>})}
            <div className="eg-guide-scheduler__matrix-heading eg-guide-scheduler__matrix-total eg-guide-scheduler__matrix-total-heading"><span>Total Hrs.</span></div>
            {orderedTeam.map((guide,index) => <React.Fragment key={guide.employee_id}>
              <div className="eg-guide-scheduler__matrix-guide"><i className={`eg-guide-avatar eg-guide-avatar--${index % 7}`}>{String(guide.employee_name || guide.name || "").split(/\s+/).map(part=>part[0]).slice(0,2).join("")}</i><span>{compactEmployeeName(guide)}{isSalariedGeneralManager(guide) && <small>GM</small>}</span></div>
              {days.map((day) => { const date = isoDate(day), entries = shiftByCell[`${guide.employee_id}|${date}`] || [undefined]; const closure=dayClosure(period.current_version,date);return <div key={date} className={`eg-calendar-cell${closure?" is-holiday":""}`} aria-label={closure?`${date}, Holiday closed`:undefined}>{closure?<span className="eg-day-closed-caption">{index===Math.floor(orderedTeam.length/2)-1?"Closed":""}</span>:entries.map((entry, entryIndex) => <ShiftCell key={entry?.id || entryIndex} shift={entry} employee={guide} dateStr={date} onSave={onSaveShift} onDelete={onDeleteShift} canEdit={canEdit} canAmend={canAmend} needsAmendment={isPublished} copiedShift={copiedShift} movingShift={movingShift} onCopyShift={(source) => setCopiedShift({ ...(source.start && source.end ? { start: source.start, end: source.end } : {}), notes: source.notes || "", break_start: source.break_start, break_end: source.break_end, location: source.location, shift_type: shiftTypeFor(source), requested: getShiftPresentation(source).requested, mod: source.mod === true })} onBeginMove={(source) => setMovingShift(source)} onPasteShift={pasteShift} onPlaceMove={placeMove} />)}</div>; })}
              <div className="eg-guide-scheduler__matrix-total eg-guide-scheduler__matrix-guide-total" aria-label={`${guide.employee_name} weekly hours`}>
                {isSalariedGeneralManager(guide) ? "Salaried" : formatHours(weeklyHoursFor(guide))}
              </div>
            </React.Fragment>)}
          </div>
        </section>
        <footer><div className="eg-focused-legend" aria-label="Shift color legend"><EGShiftLegend /></div><div>Boutique total <strong>{formatHours(countedHours)}</strong><span>Non-salaried scheduled hours</span></div></footer>
      </>}
    </div>
  );
}

function AmendmentsPanel({ periodId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!periodId) return;
    api.get(`/scheduling/periods/${periodId}/amendments`).then((r) => setItems(r.data?.amendments || [])).catch(() => setItems([]));
  }, [periodId]);
  if (items.length === 0) return null;
  return (
    <div className="bg-white subtle-border rounded-3xl p-6 card-shadow" data-testid="eg-amendments">
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif-display text-stone-900 text-xl">Published amendments</h2>
        <span className="text-[11px] label-eyebrow text-stone-400">Every edit after publish is recorded</span>
      </div>
      <ul className="mt-4 space-y-3">
        {items.map((a) => {
          const before = a.before ? `${a.before.date} · ${fmtTime(a.before.start)}–${fmtTime(a.before.end)}` : "—";
          const after = a.after ? `${a.after.date} · ${fmtTime(a.after.start)}–${fmtTime(a.after.end)}` : "removed";
          return (
            <li key={a.id} className="text-sm border-l-2 border-amber-300 pl-3" data-testid={`eg-amendment-${a.id}`}>
              <div className="text-stone-900">{before} → <span className="text-stone-700">{after}</span></div>
              <div className="text-[11px] text-stone-500 mt-0.5">
                {a.changed_by_name} · {new Date(a.changed_at).toLocaleString()} · Reason: {a.reason}
              </div>
              <div className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1">
                <ClipboardList size={11} strokeWidth={1.5} /> Zenoti update queued
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// SubmissionPanel is imported from ScheduleMT? No — duplicate to keep isolation clean, and this one can evolve.
function SubmissionPanel({ period, onSubmit, onRecall, onApprove, onRequestChanges, onPublish, onReopen }) {
  const [note, setNote] = useState("");
  const [changesNote, setChangesNote] = useState("");
  const [action, setAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const wrap = (fn) => async (...args) => { setSaving(true); try { await fn(...args); setNote(""); setChangesNote(""); setAction(null); } finally { setSaving(false); } };
  const doSubmit = wrap(async () => onSubmit(note));
  const doRecall = wrap(async () => onRecall(note));
  const doApprove = wrap(async () => onApprove(note));
  const doChanges = async () => {
    if (!changesNote.trim()) return;
    setSaving(true);
    try { await onRequestChanges(changesNote); setChangesNote(""); setAction(null); } finally { setSaving(false); }
  };
  const doPublish = wrap(async () => onPublish());
  const doReopen = wrap(async () => onReopen());

  return (
    <div className="bg-white subtle-border rounded-3xl p-6 card-shadow space-y-4" data-testid="eg-submission">
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif-display text-stone-900 text-xl">Approval trail</h2>
        <span className="text-[10px] label-eyebrow text-stone-400">Katrina primary · Rose &amp; Danny fallback</span>
      </div>
      <EGClosureHistory period={period}/>
      <div className="flex flex-wrap gap-2">
        {period.can_submit && <button onClick={() => setAction(action === "submit" ? null : "submit")} data-testid="eg-submit-toggle" className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 flex items-center gap-2"><Send size={14} strokeWidth={1.5} /> Submit to Katrina</button>}
        {period.can_recall && <button onClick={() => setAction(action === "recall" ? null : "recall")} data-testid="eg-recall-toggle" className="text-sm px-4 py-2 rounded-full bg-white subtle-border text-stone-700 hover:bg-stone-50 flex items-center gap-2"><Undo2 size={14} strokeWidth={1.5} /> Recall for revisions</button>}
        {period.can_approve && (<>
          <button onClick={() => setAction(action === "approve" ? null : "approve")} data-testid="eg-approve-toggle" className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2"><CheckCircle2 size={14} strokeWidth={1.5} /> Approve</button>
          <button onClick={() => setAction(action === "changes" ? null : "changes")} data-testid="eg-changes-toggle" className="text-sm px-4 py-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-2"><RotateCcw size={14} strokeWidth={1.5} /> Request changes</button>
        </>)}
        {period.can_publish && <button onClick={doPublish} disabled={saving} data-testid="eg-publish" className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2"><CheckCircle2 size={14} strokeWidth={1.5} /> Publish to Active</button>}
        {period.can_reopen && <button onClick={doReopen} disabled={saving} data-testid="eg-reopen" className="text-sm px-4 py-2 rounded-full bg-white subtle-border text-stone-700 hover:bg-stone-50 flex items-center gap-2"><XCircle size={14} strokeWidth={1.5} /> Reopen</button>}
      </div>
      {action === "submit" && (
        <div className="rounded-2xl bg-stone-50 p-4 space-y-2" data-testid="eg-submit-form">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="eg-submit-note" className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none bg-white" placeholder="Anything Katrina should know?" />
          <button onClick={doSubmit} disabled={saving} data-testid="eg-submit-confirm" className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">Confirm submission</button>
        </div>
      )}
      {action === "recall" && (
        <div className="rounded-2xl bg-amber-50 p-4 space-y-3" data-testid="eg-recall-form">
          <div>
            <p className="text-sm font-medium text-stone-900">Recall for revisions</p>
            <p className="mt-1 text-xs leading-relaxed text-stone-600">This returns the schedule to an editable draft. Katrina will no longer see it as awaiting review; the original submission stays in the approval trail.</p>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="eg-recall-note" className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none bg-white" placeholder="Optional note for the approval trail" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setAction(null)} disabled={saving} className="text-sm px-4 py-2 rounded-full bg-white subtle-border text-stone-700 hover:bg-stone-50 disabled:opacity-40">Cancel</button>
            <button onClick={doRecall} disabled={saving} data-testid="eg-recall-confirm" className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">Recall schedule</button>
          </div>
        </div>
      )}
      {action === "approve" && (
        <div className="rounded-2xl bg-emerald-50 p-4 space-y-2" data-testid="eg-approve-form">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="eg-approve-note" className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none bg-white" placeholder="Approval note (optional)" />
          <button onClick={doApprove} disabled={saving} data-testid="eg-approve-confirm" className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">Confirm approval</button>
        </div>
      )}
      {action === "changes" && (
        <div className="rounded-2xl bg-amber-50 p-4 space-y-2" data-testid="eg-changes-form">
          <textarea value={changesNote} onChange={(e) => setChangesNote(e.target.value)} rows={3} data-testid="eg-changes-note" className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none bg-white" placeholder="What needs to change before you approve?" />
          <button onClick={doChanges} disabled={saving || !changesNote.trim()} data-testid="eg-changes-confirm" className="text-sm px-4 py-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40">Send back for revisions</button>
        </div>
      )}
    </div>
  );
}

export default function ScheduleEG() {
  const { user } = useAuth();
  const { periodId } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const requestedMonth = searchParams.get("prepare");
  const [prepareError, setPrepareError] = useState("");
  const preparation = useRef(null);
  useEffect(() => {
    if (!requestedMonth || periodId) return;
    let active = true;
    const code = user?.boutique_code;
    const key = `${code}:${requestedMonth}`;
    if (preparation.current?.key !== key) {
      const promise = (async () => {
        if (!code || !/^\d{4}-(0[1-9]|1[0-2])-01$/.test(requestedMonth)) throw new Error("The schedule alert is missing a valid month or boutique.");
        const find = async () => (await api.get("/scheduling/periods", {params:{boutique:code,mode:"EG"}})).data?.periods?.find(p=>p.period_start === requestedMonth);
        const existing = await find();
        if (existing) return existing;
        try { return (await api.post("/scheduling/periods", {boutique_code:code,mode:"EG",period_start:requestedMonth})).data; }
        catch (error) { if (error.response?.status === 409) { const found=await find(); if(found)return found; } throw error; }
      })();
      preparation.current = {key,promise};
    }
    preparation.current.promise.then(p=>{if(active)navigate(`/scheduling/eg/${p.id}`,{replace:true});}).catch(error=>{if(active)setPrepareError(error.response?.data?.detail || error.message || "Unable to prepare this month.");});
    return ()=>{active=false;};
  },[requestedMonth,periodId,user?.boutique_code,navigate]);

  const [closureDate,setClosureDate] = useState(null);
  const [boutique, setBoutique] = useState(user?.boutique_code || "PLT");
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodError,setPeriodError] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [weekStart, setWeekStart] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportRange, setExportRange] = useState({ start: "", end: "" });
  const [showRoster, setShowRoster] = useState(false);
  const [rosterDraft, setRosterDraft] = useState([]);
  const [rosterSaving, setRosterSaving] = useState(false);
  const [rosterError, setRosterError] = useState("");
  const [selectedCell,setSelectedCell] = useState(null), [inspectorHost,setInspectorHost] = useState(null), [reviewOpen,setReviewOpen] = useState(false);
  const shiftDrafts = useRef(new Map()), rosterRef = useRef(null), rosterOpener = useRef(null), exportOpener = useRef(null);
  useEffect(() => {setSelectedCell(null);setReviewOpen(false);shiftDrafts.current.clear();}, [periodId]);
  useLayoutEffect(() => {
    if (!showRoster) return;
    rosterRef.current?.querySelector("input")?.focus();
    return () => rosterOpener.current?.focus();
  }, [showRoster]);
  const [importingSeptemberSource, setImportingSeptemberSource] = useState(false);

  const isLeadership = user && !user.boutique_code;

  useEffect(() => {
    let cancelled=false;
    setLoading(true); setPeriodError(false);setPeriods([]);setTeam([]);
    api.get("/scheduling/periods", { params: { boutique, mode: "EG" } })
      .then((r) => {if(!cancelled)setPeriods(r.data?.periods || []);})
      .catch(() => {if(!cancelled)setPeriodError(true);})
      .finally(() => {if(!cancelled)setLoading(false);});
    api.get("/scheduling/team", { params: { boutique, role: "EG" } })
      .then((r) => {if(!cancelled)setTeam(r.data?.team || []);})
      .catch(() => {if(!cancelled)setTeam([]);});
    return()=>{cancelled=true};
  }, [boutique]);

  useEffect(() => {
    if (!periodId) { setPeriod(null); return; }
    api.get(`/scheduling/periods/${periodId}`).then((r) => {
      setPeriod(r.data);
      // Default week: Monday on/before the period start
      setWeekStart(startOfWeekMonday(parseISO(r.data.period_start)));
    }).catch(() => setPeriod(null));
  }, [periodId]); // eslint-disable-line

  const refresh = async () => {
    if (periodId) {
      const r = await api.get(`/scheduling/periods/${periodId}`);
      setPeriod(r.data);
    }
    const rr = await api.get("/scheduling/periods", { params: { boutique, mode: "EG" } });
    setPeriods(rr.data?.periods || []);
  };

  const saveShift = async (payload) => {
    await api.put(`/scheduling/periods/${period.id}/eg-shift`, payload);
    await refresh();
  };
  const deleteShift = async (shiftId, reason) => {
    await api.request({
      url: `/scheduling/periods/${period.id}/eg-shift/${shiftId}`,
      method: "delete",
      data: reason ? { amendment_reason: reason } : {},
    });
    await refresh();
  };
  const copyPrevWeek = async () => {
    if (!weekStart) return;
    const source = isoDate(addDays(weekStart, -7));
    const target = isoDate(weekStart);
    const r = await api.post(`/scheduling/periods/${period.id}/eg-copy-week`, {
      source_week_start: source, target_week_start: target, skip_existing: true,
    });
    await refresh();
    alert(`Copied ${r.data.copied} shift(s), skipped ${r.data.skipped}.`);
  };
  const clearWeek = async () => {
    if (!weekStart) return;
    if (!window.confirm(`Clear all EG shifts in the week of ${isoDate(weekStart)}?`)) return;
    const r = await api.post(`/scheduling/periods/${period.id}/eg-clear-week`, { week_start: isoDate(weekStart) });
    await refresh();
    alert(`Removed ${r.data.removed} shift(s).`);
  };
  const submit = async (note) => { await api.post(`/scheduling/periods/${period.id}/submit`, { manager_note: note }); await refresh(); };
  const recall = async (note) => { await api.post(`/scheduling/periods/${period.id}/recall`, { note }); await refresh(); };
  const approve = async (notes) => { await api.post(`/scheduling/periods/${period.id}/approve`, { notes }); await refresh(); };
  const requestChanges = async (notes) => { await api.post(`/scheduling/periods/${period.id}/request-changes`, { notes }); await refresh(); };
  const publish = async () => { await api.post(`/scheduling/periods/${period.id}/publish`, {}); await refresh(); };
  const reopen = async () => { await api.post(`/scheduling/periods/${period.id}/reopen`); await refresh(); };

  const historicalPeriod = Boolean(period?.period_end && period.period_end < new Date().toLocaleDateString('en-CA', {timeZone: 'America/New_York'}));
  const activeTeam = useMemo(() => {
    if (historicalPeriod && period?.boutique_code === "PLT") return periodRoster(period.eg_roster || [], defaultPlantationRoster(team));
    if (period?.eg_roster?.length && (historicalPeriod || ["submitted", "approved", "published"].includes(period.status))) return period.eg_roster;
    const byId = new Map(team.flatMap(person => [person.employee_id, person.employee_code].filter(Boolean).map(id => [String(id), person])));
    // Saved periods remain immutable; only current/future selectable people are projected.
    if (period?.eg_roster?.length) return period.eg_roster.flatMap(saved => {
      const current = byId.get(String(saved.employee_id));
      // Directory metadata can change; saved shifts still reference the roster ID.
      return current ? [{ ...current, ...saved }] : [];
    });
    return team;
  }, [period?.boutique_code, period?.status, historicalPeriod, period?.eg_roster, team]);

  const importSeptember2026Source = async () => {
    const sourceKeyFor = (person) => rosterNameKey(person).replace(/[^a-z0-9]/g, "");
    const employees = new Map([...team, ...activeTeam]
      .filter((person) => person?.employee_id && !String(person.employee_id).startsWith("plt-eg-"))
      .map((person) => [sourceKeyFor(person), person]));
    Object.entries(PLANTATION_EG_DIRECTORY_IDS).forEach(([key, employee_id]) => employees.set(key, { employee_id }));
    const salariedGuide = activeTeam.find(isSalariedGeneralManager);
    if (salariedGuide) employees.set("damon", salariedGuide);
    const sourcePeople = new Set(SEP_2026_SOURCE_WEEKS.flatMap(([, rows]) => Object.keys(rows)));
    const missing = [...sourcePeople].filter((key) => !employees.has(key));
    if (missing.length) { alert(`Cannot import until these roster records are available: ${missing.join(", ")}.`); return; }
    setImportingSeptemberSource(true);
    try {
      let target = periods.find((item) => item.boutique_code === "PLT" && item.mode === "EG" && item.period_start === "2026-09-01");
      if (!target) {
        try {
          const created = await api.post("/scheduling/periods", { boutique_code: "PLT", mode: "EG", period_start: "2026-09-01" });
          target = created.data;
        } catch (e) {
          if (e.response?.status !== 409) throw e;
          const existing = await api.get("/scheduling/periods", { params: { boutique: "PLT", mode: "EG" } });
          target = (existing.data?.periods || []).find((item) => item.period_start === "2026-09-01");
          if (!target) throw e;
        }
      }
      const entries = SEP_2026_SOURCE_WEEKS.flatMap(([weekStart, rows]) => Object.entries(rows).flatMap(([personKey, days]) => {
        const employee = employees.get(personKey);
        return days.map((code, dayIndex) => ({
          employee_id: employee.employee_id,
          date: isoDate(addDays(parseISO(weekStart), dayIndex)),
          ...SEP_2026_SHIFT_CODES[code],
        }));
      }));
      await api.post(`/scheduling/periods/${target.id}/eg-import`, {
        source_label: "PLT EG Schedule September 2026 PDF",
        entries,
        cleanup_fallback_roster: true,
      });
      await refresh();
      navigate(`/scheduling/eg/${target.id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not import the approved September schedule.");
    } finally {
      setImportingSeptemberSource(false);
    }
  };

  const openRoster = () => {
    rosterOpener.current = document.activeElement;
    setRosterDraft(activeTeam.map((guide, index) => ({
      id: guide.employee_id || `existing-${index}`,
      name: guide.employee_name || guide.name || "",
      picture: guide.picture,
    })));
    setRosterError("");
    setShowRoster(true);
  };

  const saveRoster = async () => {
    const guides = rosterDraft
      .map((guide) => ({ ...guide, name: guide.name.trim() }))
      .filter((guide) => guide.name);
    if (!guides.length) {
      setRosterError("Add at least one Experience Guide.");
      return;
    }
    const duplicate = guides.find((guide, index) => guides.findIndex((other) => other.name.toLowerCase() === guide.name.toLowerCase()) !== index);
    if (duplicate) {
      setRosterError(`${duplicate.name} appears more than once.`);
      return;
    }
    const roster = guides.map((guide) => {
      const existing = activeTeam.find((person) => person.employee_id === guide.id);
      const directoryMatch = team.find((person) => person.employee_id === guide.id)
        || (historicalPeriod ? team.find((person) => rosterNameKey(person) === guide.name.split(/\s+/)[0].toLowerCase()) : null);
      const source = existing || directoryMatch;
      return {
        employee_id: source?.employee_id || guide.id || `schedule-eg-${period.id}-${guide.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        employee_name: historicalPeriod ? guide.name : source?.employee_name || guide.name,
        picture: guide.picture || source?.picture,
      };
    });
    setRosterSaving(true);
    setRosterError("");
    try {
      const result = await api.put(`/scheduling/periods/${period.id}/eg-roster`, { roster });
      setPeriod((current) => ({ ...current, eg_roster: result.data.team }));
      setShowRoster(false);
    } catch (e) {
      setRosterError(e.response?.data?.detail || "Could not save this roster.");
    } finally {
      setRosterSaving(false);
    }
  };

  const deadline = period?.deadline;

  useEffect(() => {
    setShowExport(false);
    setExportRange({ start: period?.period_start || "", end: period?.period_end || "" });
  }, [period?.id, period?.period_start, period?.period_end]);

  useVesselNavigation({label:'Scheduling',tools:[]});
  const scheduleTools = period&&weekStart?[
    {id:'export',label:'Export schedule',Icon:FileDown,onSelect:()=>{exportOpener.current=document.activeElement;setShowExport(true);}},
    ...(period.boutique_code==='PLT'?[{id:'import',Icon:Upload,label:importingSeptemberSource?'Importing schedule…':'Import schedule',disabled:importingSeptemberSource,onSelect:importSeptember2026Source}]:[]),
    ...(period.can_edit?[{id:'roster',label:'Edit roster',Icon:Users,onSelect:openRoster},{id:'copy',label:'Copy prior week',Icon:Copy,onSelect:copyPrevWeek},{id:'clear',label:'Clear week',Icon:Eraser,onSelect:clearWeek}]:[]),
  ]:[];
  return (
    <main className={`toto-workspaces toto-eg-schedule${periodId ? " is-editor" : ""}`}>
      {closureDate&&period&&<EGDayClosure key={closureDate} period={period} date={closureDate} onClose={()=>setClosureDate(null)} onRefresh={refresh} onSave={async payload=>{const result=await api.put(`/scheduling/periods/${period.id}/eg-day-closure`,payload);setPeriod(result.data);await refresh();}}/>}
      <section className="toto-workspaces__vessel toto-eg-schedule__vessel" aria-label="Experience Guide schedule workspace">
      <div className="eg-schedule-space__content">
        <div className="eg-schedule-space__heading flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif-display text-stone-900 text-4xl sm:text-6xl " style={{ fontWeight: 400 }}>
              Experience Guide schedules
            </h1>
          </div>
          <div className="eg-calendar-date-group">
            {!periodId && isLeadership && <select aria-label="Schedule boutique" value={boutique} onChange={event=>setBoutique(event.target.value)}>{SCHEDULE_BOUTIQUES.map(item=><option key={item.code} value={item.code}>{item.name || item.label || item.code}</option>)}</select>}
            <time className="eg-calendar-date" dateTime={isoDate(new Date())}>{new Date().toLocaleDateString("en-US", {weekday:"long",month:"long",day:"numeric",year:"numeric"})}</time>
          </div>
        </div>

        {periodId && showNew && <PeriodPicker boutique={period?.boutique_code || boutique} periods={periods} onCreated={p=>{setShowNew(false);setWeekStart(startOfWeekMonday(parseISO(p.period_start)));navigate(`/scheduling/eg/${p.id}`);}} onCancel={()=>setShowNew(false)}/> }
        {/* Period list / picker */}
        {requestedMonth && !periodId && <p role="status">{prepareError || "Opening the upcoming month’s schedule…"}</p>}
        {!periodId && !requestedMonth && <>
          {showNew && <PeriodPicker boutique={period?.boutique_code || boutique} periods={periods} onCreated={p=>{setShowNew(false);navigate(`/scheduling/eg/${p.id}`);}} onCancel={()=>setShowNew(false)}/>}
          <EGScheduleOverview boutique={boutique} periods={periods} team={team} loading={loading} periodError={periodError} onChooseMonth={()=>setShowNew(v=>!v)}/>
        </>}

        {/* Detail */}
        {periodId && period && weekStart && (
          <InspectorContext.Provider value={{periodId:period.id,selected:selectedCell,select:key=>{setSelectedCell(key);if(key)setReviewOpen(false);},host:inspectorHost,drafts:shiftDrafts}}>
          <div className={`mt-8 space-y-5 eg-schedule-detail${showExport ? " is-exporting" : ""}`}>
            {showExport && <EGScheduleExport key={period.id} period={period} weekStart={weekStart} range={exportRange} onRangeChange={setExportRange} onClose={()=>{setShowExport(false);exportOpener.current?.focus();}} onPrint={()=>window.print()}/>}
            <div className={`eg-focused-schedule${period.status === "published" ? " is-published" : ""}${selectedCell || reviewOpen ? " has-inspector" : ""}`}>
            <div className="eg-focused-schedule__ledger">
            <div className="rounded-3xl p-6 border border-stone-200/80 bg-white/70" data-testid="eg-period-header">
              <div className="eg-schedule-context-rail">
                <button type="button" className="eg-calendar-month" aria-label="Choose schedule month" aria-expanded={showNew} onClick={()=>setShowNew(v=>!v)}>{period.label}<ChevronDown size={16}/></button>
                <div className="eg-schedule-context-rail__week">
                  <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="eg-week-prev" aria-label="Previous week"><ChevronLeft size={16} strokeWidth={1.5} /></button>
                  <span data-testid="eg-week-label">{weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {addDays(weekStart, 6).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="eg-week-next" aria-label="Next week"><ChevronRight size={16} strokeWidth={1.5} /></button>
                </div>
                <span className="eg-focused-status">{(STATUS_STYLES[period.status] || {}).label || period.status}</span>
                {deadline && (
                  <div className="eg-schedule-context-rail__deadline" data-testid="eg-deadline-pill">
                    {deadline.tone === "overdue" ? <AlertTriangle size={12} strokeWidth={1.5} /> : <Info size={12} strokeWidth={1.5} />}
                    <span>{deadline.text}</span>
                  </div>
                )}
                <nav className="eg-schedule-context-rail__actions" aria-label="Schedule tools">
                  {scheduleTools.filter(tool=>["export","roster"].includes(tool.id)).map(tool=><button key={tool.id} type="button" aria-label={tool.label} disabled={tool.disabled} onClick={tool.onSelect}><tool.Icon size={16}/>{tool.id === "export" ? "Export" : "Edit roster"}</button>)}
                  {scheduleTools.some(tool=>!["export","roster"].includes(tool.id)) && <details className="eg-focused-more" onKeyDown={event=>{if(event.key==="Escape"){event.currentTarget.open=false;event.currentTarget.querySelector("summary")?.focus();}}}><summary>More tools <ChevronDown size={14}/></summary><div>{scheduleTools.filter(tool=>!["export","roster"].includes(tool.id)).map(tool=><button key={tool.id} type="button" disabled={tool.disabled} onClick={tool.onSelect}><tool.Icon size={14}/>{tool.label}</button>)}</div></details>}
                </nav>

              </div>
              {period.status === "published" && (
                <div className="mt-4 rounded-2xl bg-emerald-50 text-emerald-800 text-xs px-3 py-2 flex items-start gap-2" data-testid="eg-published-banner">
                  <Info size={12} strokeWidth={1.5} className="mt-0.5" />
                  <div>Published to Active. Edits require an amendment reason and auto-queue a Zenoti update.</div>
                </div>
              )}
            </div>

            <div className="bg-white border border-stone-200/80 rounded-3xl overflow-hidden shadow-[0_14px_38px_rgba(67,61,48,.06)]" data-testid="eg-grid">
              <WeekGrid key={period.id}
                period={period}
                team={activeTeam}
                weekStart={weekStart}
                canEdit={period.can_edit}
                canAmend={period.can_amend}
                isPublished={period.status === "published"}
                onDayOptions={setClosureDate}
                onSaveShift={saveShift}
                onDeleteShift={deleteShift}
              />
            </div>

            <p className="eg-focused-scroll-cue">Scroll the schedule to see every staff member and day.</p>
            </div>
            <aside className="eg-focused-schedule__inspector" ref={setInspectorHost} hidden={!selectedCell && !reviewOpen}>
              <div hidden={!reviewOpen} className="eg-focused-review">
                <button type="button" aria-label="Close schedule review" onClick={()=>setReviewOpen(false)}><XCircle size={18}/></button>
                <SubmissionPanel key={period.id} period={period} onSubmit={submit} onRecall={recall} onApprove={approve} onRequestChanges={requestChanges} onPublish={publish} onReopen={reopen} />
                <AmendmentsPanel periodId={period.id} />
              </div>
            </aside>
            <footer className="eg-focused-footer"><span>{period.label} · {(STATUS_STYLES[period.status] || {}).label || period.status}{deadline && ` · ${deadline.text}`}</span><button type="button" onClick={()=>{setSelectedCell(null);setReviewOpen(true);}}>Approval trail</button><button type="button" onClick={()=>{setSelectedCell(null);setReviewOpen(true);}}>Review &amp; submit</button></footer>
            </div>

            <EGPrintPacket period={period} team={activeTeam} startDate={exportRange.start || period.period_start} endDate={exportRange.end || period.period_end} />

            {showRoster && (
              <div ref={rosterRef} onKeyDown={event=>{if(event.key==="Escape"){event.stopPropagation();setShowRoster(false);}if(event.key==="Tab"){const controls=[...event.currentTarget.querySelectorAll("button:not(:disabled),input:not(:disabled)")],first=controls[0],last=controls.at(-1);if(event.shiftKey && document.activeElement===first){event.preventDefault();last?.focus();}else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first?.focus();}}}} className="fixed inset-0 z-50 bg-stone-950/20 backdrop-blur-sm grid place-items-center p-5" role="dialog" aria-modal="true" aria-label="Experience Guide roster">
                <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl border border-stone-200">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] label-eyebrow text-stone-400">This monthly schedule</div>
                      <h3 className="font-serif-display text-stone-900 text-2xl mt-1">Experience Guide roster</h3>
                    </div>
                    <button onClick={() => setShowRoster(false)} className="text-stone-400 hover:text-stone-800 p-1" aria-label="Close roster editor"><XCircle size={19} strokeWidth={1.5} /></button>
                  </div>
                  <p className="text-sm text-stone-600 mt-3">Edit each name directly, including last names. This changes only this EG schedule and never payroll or the employee directory.</p>
                  <div className="mt-5 space-y-2" data-testid="eg-roster-names">
                    {rosterDraft.map((guide, index) => (
                      <div key={guide.id || index} className="flex items-center gap-2">
                        {historicalPeriod ? <input value={guide.name} onChange={(event) => setRosterDraft((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))}
                          aria-label={`Experience Guide ${index + 1} name`}
                          className="min-w-0 flex-1 rounded-xl subtle-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-stone-200" /> :
                          <select value={guide.id} aria-label={`Experience Guide ${index + 1}`} onChange={event => {
                            const person = team.find(item => item.employee_id === event.target.value);
                            if (person) setRosterDraft(current => current.map((item, itemIndex) => itemIndex === index ? {id:person.employee_id,name:person.employee_name,picture:person.picture} : item));
                          }} className="min-w-0 flex-1 rounded-xl subtle-border px-3 py-2.5 text-sm">
                            {team.filter(person => person.employee_id === guide.id || !rosterDraft.some(item => item.id === person.employee_id)).map(person => <option key={person.employee_id} value={person.employee_id}>{person.employee_name}</option>)}
                          </select>}
                        <button onClick={() => setRosterDraft((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="p-2 text-stone-400 hover:text-rose-600" aria-label={`Remove ${guide.name || "Experience Guide"}`}><Trash2 size={16} strokeWidth={1.5} /></button>
                      </div>
                    ))}
                    <button disabled={!historicalPeriod && !team.some(person => !rosterDraft.some(item => item.id === person.employee_id))} onClick={() => setRosterDraft(current => {
                      if (historicalPeriod) return [...current, {id:`new-${Date.now()}`,name:""}];
                      const person = team.find(item => !current.some(guide => guide.id === item.employee_id));
                      return person ? [...current, {id:person.employee_id,name:person.employee_name,picture:person.picture}] : current;
                    })} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-full bg-stone-50 text-stone-700 hover:bg-stone-100"><Plus size={15} strokeWidth={1.5} /> Add Experience Guide</button>
                  </div>
                  {rosterError && <div className="mt-3 text-sm text-rose-600" data-testid="eg-roster-error">{rosterError}</div>}
                  <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setShowRoster(false)} className="text-sm px-4 py-2 rounded-full hover:bg-stone-50">Cancel</button>
                    <button onClick={saveRoster} disabled={rosterSaving} data-testid="eg-save-roster" className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">{rosterSaving ? "Saving…" : "Save roster"}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          </InspectorContext.Provider>
        )}
      </div>
      </section>
      <TotoAdaptiveDock active="workspace" />
    </main>
  );
}
